import cards from 'card-validator'
import { inherits } from 'util'
import * as Yup from 'yup'

const paymenTypeSchema = Yup.string()
  .oneOf(['CreditCard', 'ACH', 'None', 'Cash'], 'Invalid payment type')
  .required('Payment type is required')
  .default('CreditCard')

type PaymentTypeSchema = Yup.InferType<typeof paymenTypeSchema>
const validateCreditCardExpirationDate = (value: string | undefined) => {
  if (!value) return false
  const [month, year] = value.split('/')
  const currentYear = new Date().getFullYear() % 100 // Get last two digits of the year
  const currentMonth = new Date().getMonth() + 1 // JS months are 0-indexed
  const expYear = parseInt(year, 10)
  const expMonth = parseInt(month, 10)

  if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
    return false // Expiration date is in the past
  }
  if (expMonth > 12 || expMonth < 1) {
    return false // Invalid month
  }
  return true
}

export const paymentFormValidator = Yup.object().shape({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  subtotal: Yup.number(),
  fees: Yup.number(),
  total: Yup.number(),
  merchantId: Yup.string(),
  paymentType: paymenTypeSchema,
  cardHolderName: Yup.string().when('paymentType', {
    is: 'CreditCard',
    then: (schema) => schema.required('Card holder name is required').default(''),
    otherwise: (schema) => schema.optional(),
  }),
  cardNumber: Yup.string().when('paymentType', {
    is: 'CreditCard',
    then: (schema) =>
      schema
        .required('Card number is required')
        .test('cardNumber', 'Card number is invalid', (x) => cards.number(x).isValid)
        .default(''),
    otherwise: (schema) => schema.optional(),
  }),
  cardType: Yup.string().when('paymentType', {
    is: 'CreditCard',
    then: (schema) =>
      schema
        .oneOf(['Visa', 'MasterCard', 'AMEX', 'Discover', 'DinersClub', 'JCB'], 'Invalid card type')
        .required('Card type is required'),
    otherwise: (schema) => schema.optional(),
  }),
  expirationDate: Yup.string().when('paymentType', {
    is: 'CreditCard',
    then: (schema) =>
      schema
        .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Invalid date format')
        .test('is-expiration-date-valid', 'Credit card is expired', validateCreditCardExpirationDate)
        .required('Required'),
    otherwise: (schema) => schema.optional(),
  }),
  cvv: Yup.string().when('paymentType', {
    is: 'CreditCard',
    then: (schema) => schema.required('CVV is required'),
    otherwise: (schema) => schema.optional(),
  }),
  zipCode: Yup.string().when('paymentType', {
    is: 'CreditCard',
    then: (schema) => schema.required('ZIP is required'),
    otherwise: (schema) => schema.optional(),
  }),
  nameOnBankAccount: Yup.string().when('paymentType', {
    is: 'ACH',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  bankAccountType: Yup.string()
    .when('paymentType', {
      is: 'ACH',
      then: (schema) => schema.oneOf(['Checking', 'Savings'], 'Invalid bank account type').required(),
      otherwise: (schema) => schema.optional(),
    })
    .default('Checking'),
  bankAccountNumber: Yup.string().when('paymentType', {
    is: 'ACH',
    then: (schema) => schema.min(5, 'Account number must be at least 5 digits').required('Account number is required'),
    otherwise: (schema) => schema.optional(),
  }),
  routingNumber: Yup.string().when('paymentType', {
    is: 'ACH',
    then: (schema) => schema.min(9, 'Routing number must be 9 digits').required('Routing number is required'),
    otherwise: (schema) => schema.optional(),
  }),
})

export interface PaymentFormSchema extends Yup.InferType<typeof paymentFormValidator> {}
